import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useTranslation } from 'hooks/useTranslation';
import {
  FeedListItem,
  LoaderDashboard,
  DashboardHeading,
  FeedListInfinitySpinner,
} from 'components';
import { handleFetchMoreError, criticalFeedError } from 'services/apollo';
import { FolderFeedQuery } from 'services/apollo/queries/folderFeedQueries';
import { useParams } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useFeatureFlagState } from 'context';
import styles from './FolderFeed.module.scss';
import { FolderBack, FolderBreadcrumbs } from 'screens/FolderScreen/components';

export const FolderFeed = () => {
  const { folderId } = useParams();
  const { groupsFeatureFlag } = useFeatureFlagState();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { error, data, fetchMore } = useQuery(FolderFeedQuery, {
    variables: { nodeId: folderId, first: 20, cursor: '', includeGroups: groupsFeatureFlag },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const [ref, inView] = useInView({
    rootMargin: '500px 0px',
    threshold: 0,
  });

  const { t } = useTranslation();

  const triggerFetchMore = async () => {
    setIsLoadingMore(true);
    const { endCursor } = data.nodeById.children.pageInfo;
    try {
      await fetchMore({
        variables: { cursor: endCursor },
        updateQuery: (prevResult: any, { fetchMoreResult }) => {
          fetchMoreResult.nodeById.children.nodes = [
            ...prevResult.nodeById.children.nodes,
            ...fetchMoreResult.nodeById.children.nodes,
          ];
          setIsLoadingMore(false);
          return fetchMoreResult;
        },
      });
    } catch (e) {
      handleFetchMoreError(e);
    }
  };

  useEffect(() => {
    if (inView === true) {
      triggerFetchMore().then((response) => response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  if (data === undefined) return <LoaderDashboard layout="Feed" />;

  if (error && criticalFeedError(error)) {
    return <LoaderDashboard layout="Feed" />;
  }

  if (data?.nodeById?.id !== folderId) {
    return (
      <>
        <FolderBreadcrumbs />
        <div className={styles.heading}>
          <DashboardHeading level={1}>
            {data.nodeById.children.nodes.length} {t('folder-screen.page-heading')}
          </DashboardHeading>
        </div>
        <FolderBack />
        <LoaderDashboard layout="HeadlessFeed" />
      </>
    );
  }

  return (
    <>
      <FolderBreadcrumbs />
      <div className={styles.heading}>
        <DashboardHeading level={1}>
          {data?.nodeById?.children?.pageInfo?.total} {t('folder-screen.page-heading')}
        </DashboardHeading>
      </div>
      <FolderBack />

      {data.nodeById.children.nodes.map((node: any, key: number) => (
        <FeedListItem
          nodeType={node.__typename}
          nodeId={node.id}
          key={key}
          name={node.title}
          baseUrl={node.baseUrl}
          url={node.url}
          owner={node.owners[0]}
          collaborators={node.accessorInfo}
          modified={node.modified}
          created={node.created}
          nodeIcon={node.category}
          isIntegrationConnected={node.isIntegrationConnected}
          integrationType={node.integrationType}
          isShareable={node.sharingEnabled}
          shareTargets={node.shareTargets}
        />
      ))}
      {data.nodeById.children.pageInfo.hasNextPage && (
        <div ref={ref}>
          <FeedListInfinitySpinner isLoadingMore={isLoadingMore} />
        </div>
      )}
    </>
  );
};
